import React, { useEffect, useState } from 'react';
import { Briefcase, Calendar, Mail, MapPin, Clock } from 'lucide-react';
import { careersService } from '../services/api';

export interface Career {
  id: number;
  position_title: string;
  job_type: 'fulltime' | 'parttime';
  qualifications: string;
  experience: string | null;
  location_type: 'onsite' | 'virtual';
  skills_required: string[];
  added_advantage: string | null;
  application_deadline: string;
  comments: string | null;
  application_email: string;
}

const CareersPage: React.FC = () => {
  const [careers, setCareers] = useState<Career[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const response = await careersService.getOpenCareers();
        setCareers(response.data || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load careers');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const applyLink = (career: Career) =>
    `mailto:${career.application_email}?subject=${encodeURIComponent(`Application for ${career.position_title}`)}`;

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Careers</h1>
        <p className="text-gray-600 mt-1">Open positions currently accepting applications</p>
      </div>

      {loading && <p className="text-gray-600">Loading careers...</p>}

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      {!loading && !error && careers.length === 0 && (
        <div className="rounded-lg bg-white p-8 text-center shadow">
          <Briefcase className="mx-auto h-10 w-10 text-gray-400 mb-3" />
          <p className="text-gray-700 font-medium">No careers are currently available.</p>
          <p className="text-gray-500 text-sm">Please check back later.</p>
        </div>
      )}

      <div className="space-y-6">
        {careers.map((career) => (
          <div key={career.id} className="rounded-lg bg-white p-6 shadow">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">{career.position_title}</h2>
                <div className="mt-2 flex flex-wrap gap-3 text-sm text-gray-600">
                  <span className="inline-flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    {career.job_type === 'fulltime' ? 'Full-time' : 'Part-time'}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    {career.location_type === 'onsite' ? 'On-site' : 'Virtual'}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    Deadline: {new Date(career.application_deadline).toLocaleString()}
                  </span>
                </div>
              </div>
              <a
                href={applyLink(career)}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                <Mail className="h-4 w-4" />
                Apply
              </a>
            </div>

            <dl className="mt-4 space-y-3 text-sm">
              <div>
                <dt className="font-medium text-gray-900">Qualifications</dt>
                <dd className="text-gray-700 whitespace-pre-line">{career.qualifications}</dd>
              </div>
              {career.experience && (
                <div>
                  <dt className="font-medium text-gray-900">Experience</dt>
                  <dd className="text-gray-700 whitespace-pre-line">{career.experience}</dd>
                </div>
              )}
              {career.skills_required?.length > 0 && (
                <div>
                  <dt className="font-medium text-gray-900">Skills required</dt>
                  <dd className="mt-1 flex flex-wrap gap-2">
                    {career.skills_required.map((skill) => (
                      <span key={skill} className="rounded-full bg-blue-50 px-3 py-1 text-xs text-blue-700">
                        {skill}
                      </span>
                    ))}
                  </dd>
                </div>
              )}
              {career.added_advantage && (
                <div>
                  <dt className="font-medium text-gray-900">Added advantage</dt>
                  <dd className="text-gray-700 whitespace-pre-line">{career.added_advantage}</dd>
                </div>
              )}
              {career.comments && (
                <div>
                  <dt className="font-medium text-gray-900">Notes</dt>
                  <dd className="text-gray-700 whitespace-pre-line">{career.comments}</dd>
                </div>
              )}
            </dl>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CareersPage;
